import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

import { apiUrl } from "../commons/inFormTypes";
import AuthLayout from "../components/User/AuthLayout";
import GoogleLoginButton from "../components/User/GoogleLoginButton";
import { useLanguage } from "../i18n/LanguageContext";
import { siteUrl } from "../config/urls";


const SignUp = () => {
    const [form, setForm] = useState({ name: "", family: "", email: "", password: "", confirmPassword: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const requestedRedirect = searchParams.get("redirect");
    const redirectTo = requestedRedirect?.startsWith("/") && !requestedRedirect.startsWith("//") ? requestedRedirect : "/user/dashboard";
    const { isEnglish } = useLanguage();
    const t = isEnglish ? { short: "Password must be at least 6 characters.", mismatch: "The passwords do not match.", failed: "Registration failed. This email may already be in use.", existing: "Already have an account?", signIn: "Sign in", title: "Create your", account: "Infinity Apply account", intro: "Create your account and begin your academic or professional opportunity journey.", first: "First name", last: "Last name", email: "Email address", password: "Password", passwordPh: "At least 6 characters", confirm: "Confirm password", confirmPh: "Enter the password again", hide: "Hide password", show: "Show password", legal1: "By creating an account, you accept Infinity Apply's", terms: "Terms of Service", legal2: "and", privacy: "Privacy Policy", loading: "Creating account...", submit: "Create account", or: "or" } : { short: "رمز عبور باید حداقل ۶ کاراکتر باشد.", mismatch: "رمز عبور و تکرار آن یکسان نیستند.", failed: "ثبت‌نام انجام نشد. ممکن است این ایمیل قبلاً استفاده شده باشد.", existing: "قبلاً ثبت‌نام کرده‌اید؟", signIn: "وارد شوید", title: "ثبت‌نام در", account: "اینفینیتی اپلای", intro: "حساب خود را بسازید و مسیر فرصت‌های تحصیلی و حرفه‌ای را شروع کنید.", first: "نام", last: "نام خانوادگی", email: "آدرس ایمیل", password: "رمز عبور", passwordPh: "حداقل ۶ کاراکتر", confirm: "تکرار رمز عبور", confirmPh: "تکرار رمز عبور", hide: "پنهان کردن رمز عبور", show: "نمایش رمز عبور", legal1: "با ساخت حساب،", terms: "شرایط استفاده", legal2: "و", privacy: "سیاست حریم خصوصی", loading: "در حال ساخت حساب...", submit: "ساخت حساب کاربری", or: "یا" };

    const updateField = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError("");

        if (form.password.length < 6) {
            setError(t.short);
            return;
        }
        if (form.password !== form.confirmPassword) {
            setError(t.mismatch);
            return;
        }

        setIsLoading(true);
        try {
            await axios.post(`${apiUrl}user/InsertUser`, {
                Email: form.email.trim(),
                Name: form.name.trim(),
                Family: form.family.trim(),
                Password: form.password,
            });

            navigate(`/SignIn?registered=1&redirect=${encodeURIComponent(redirectTo)}`, { replace: true });
        } catch {
            setError(t.failed);
        } finally {
            setIsLoading(false);
        }
    };

    const complete = form.name && form.family && form.email && form.password && form.confirmPassword;

    return (
        <AuthLayout mode="signUp">
            <p className="auth-switch">
                {t.existing}
                <Link to={`/SignIn?redirect=${encodeURIComponent(redirectTo)}`}>{t.signIn}</Link>
            </p>

            <header className="auth-heading">
                <h1>{t.title} <span>{t.account}</span></h1>
                <p>{t.intro}</p>
            </header>

            <form className="auth-form" onSubmit={handleSubmit}>
                <div className="auth-row">
                    <div className="auth-field">
                        <label htmlFor="signup-name">{t.first}</label>
                        <div className="auth-input-wrap"><input id="signup-name" type="text" value={form.name} onChange={updateField("name")} placeholder={t.first} autoComplete="given-name" required /></div>
                    </div>
                    <div className="auth-field">
                        <label htmlFor="signup-family">{t.last}</label>
                        <div className="auth-input-wrap"><input id="signup-family" type="text" value={form.family} onChange={updateField("family")} placeholder={t.last} autoComplete="family-name" required /></div>
                    </div>
                </div>

                <div className="auth-field">
                    <label htmlFor="signup-email">{t.email}</label>
                    <div className="auth-input-wrap"><input id="signup-email" type="email" value={form.email} onChange={updateField("email")} placeholder="your.email@example.com" autoComplete="email" dir="ltr" required /></div>
                </div>

                <div className="auth-row">
                    <div className="auth-field">
                        <label htmlFor="signup-password">{t.password}</label>
                        <div className="auth-input-wrap auth-input-wrap--password">
                            <input id="signup-password" type={showPassword ? "text" : "password"} value={form.password} onChange={updateField("password")} placeholder={t.passwordPh} autoComplete="new-password" dir="ltr" minLength="6" required />
                            <button className="auth-password-toggle" type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? t.hide : t.show}>{showPassword ? <EyeOff size={19} /> : <Eye size={19} />}</button>
                        </div>
                    </div>
                    <div className="auth-field">
                        <label htmlFor="signup-confirm">{t.confirm}</label>
                        <div className="auth-input-wrap"><input id="signup-confirm" type={showPassword ? "text" : "password"} value={form.confirmPassword} onChange={updateField("confirmPassword")} placeholder={t.confirmPh} autoComplete="new-password" dir="ltr" required /></div>
                    </div>
                </div>

                <p className="auth-terms">{t.legal1} <a href={siteUrl("/terms-of-service")} target="_blank" rel="noreferrer">{t.terms}</a> {t.legal2} <a href={siteUrl("/privacy-policy")} target="_blank" rel="noreferrer">{t.privacy}</a>{!isEnglish && " اینفینیتی اپلای را می‌پذیرید."}</p>
                {error && <p className="auth-error" role="alert">{error}</p>}

                <button className="auth-submit" type="submit" disabled={isLoading || !complete}>
                    {isLoading && <span className="auth-spinner" aria-hidden="true" />}
                    {isLoading ? t.loading : t.submit}
                </button>

                <div className="auth-divider">{t.or}</div>
                <div className="auth-google"><GoogleLoginButton text="signup_with" redirectTo={redirectTo} /></div>
            </form>
        </AuthLayout>
    );
};

export default SignUp;
